/**
 * Alert Toast Bridge
 *
 * Bridges WebSocket alerts to the Toast notification system.
 * Shows a toast when a real-time alert is received.
 */

import { useEffect } from 'react';
import { useAlerts, type Alert } from '../contexts/AlertsContext';
import { useToast } from './Toast';

/**
 * Map alert types to toast types
 */
function getToastType(alertType: Alert['type']): 'success' | 'error' | 'warning' | 'info' {
  switch (alertType) {
    case 'price_alert':
      return 'warning';
    case 'whale_activity':
      return 'info';
    case 'new_token':
      return 'success';
    case 'graduation':
      return 'success';
    default:
      return 'info';
  }
}

/**
 * Format alert for toast display
 */
function formatAlertMessage(alert: Alert): string {
  // Keep it short for toast
  const shortMessage = alert.message.length > 80
    ? alert.message.slice(0, 77) + '...'
    : alert.message;
  return shortMessage;
}

export function AlertToastBridge() {
  const { alerts } = useAlerts();
  const { showToast } = useToast();

  // Keep track of which alerts we've shown toasts for
  useEffect(() => {
    if (alerts.length === 0) return;

    // Only show toast for the most recent alert
    const latestAlert = alerts[alerts.length - 1];
    if (!latestAlert) return;

    // Check if this alert was just added (within last 2 seconds)
    const isRecent = Date.now() - latestAlert.timestamp < 2000;
    if (!isRecent) return;

    // Show the toast
    showToast(
      getToastType(latestAlert.type),
      formatAlertMessage(latestAlert),
      5000 // 5 seconds
    );
  }, [alerts.length]); // Only trigger when alerts count changes

  return null; // This component doesn't render anything
}
